import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Main } from '@/components/layout/main';

import { superAdminService } from '@/api/super-admin';
import { SuperAdminCabinet, SuperAdminCabinetCreate, SuperAdminCabinetStats, CabinetApprovalStatus } from '@/types/super-admin';
import { handleServerError } from '@/utils/handle-server-error';
import {
  Building2,
  Users,
  FileText,
  MoreHorizontal,
  Eye,
  Trash2,
  PowerOff,
  Power,
  Plus,
  HardDrive,
  CheckCircle2,
  XCircle,
  Clock3,
  Phone,
  Mail,
  User as UserIcon,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DataTable } from '@/components/table';
import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@/components/data-table-column-header';
import { PageHeader } from '@/components/layout/page-header';

export default function CabinetsManagement() {
  const { t } = useTranslation();
  const [cabinets, setCabinets] = useState<SuperAdminCabinet[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagesCnt, setPagesCnt] = useState(1);
  // Newest first: the queue is worked from the most recent signup down.
  const [params, setParams] = useState<{ page: number; page_size: number; ordering?: string | null }>({ page: 0, page_size: 10, ordering: '-created_at' });
  const [selectedCabinet, setSelectedCabinet] = useState<SuperAdminCabinetStats | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showToggleDialog, setShowToggleDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [actionCabinet, setActionCabinet] = useState<SuperAdminCabinet | null>(null);
  const [newCabinet, setNewCabinet] = useState<SuperAdminCabinetCreate>({ name: '' });
  const [toggleReason, setToggleReason] = useState('');
  // This screen exists to action signups, so it opens on what needs a decision.
  const [approvalFilter, setApprovalFilter] = useState<CabinetApprovalStatus | 'all'>('pending');
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve');
  const [approvalReason, setApprovalReason] = useState('');

  useEffect(() => {
    fetchCabinets();
  }, [params, approvalFilter]);

  const fetchCabinets = async () => {
    try {
      setLoading(true);
      const data = await superAdminService.getCabinets({
        page: params.page,
        page_size: params.page_size,
        ordering: params.ordering || undefined,
        approval_status: approvalFilter === 'all' ? undefined : approvalFilter,
      });
      if (Array.isArray(data)) {
        setCabinets(data);
        setPagesCnt(1);
      } else {
        const list = (data as any)?.results || [];
        const count = (data as any)?.count || list.length;
        setCabinets(list);
        setPagesCnt(Math.ceil(count / params.page_size));
      }
    } catch (error) {
      console.error('Error fetching cabinets:', error);
      handleServerError(error);
      setCabinets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCabinet = async () => {
    try {
      await superAdminService.createCabinet(newCabinet);
      setShowCreateDialog(false);
      setNewCabinet({ name: '' });
      fetchCabinets();
    } catch (error) {
      console.error('Error creating cabinet:', error);
      handleServerError(error);
    }
  };

  const handleViewCabinet = async (cabinet: SuperAdminCabinet) => {
    try {
      const details = await superAdminService.getCabinet(cabinet.id);
      setSelectedCabinet(details);
      setShowDetailsDialog(true);
    } catch (error) {
      console.error('Error fetching cabinet details:', error);
      handleServerError(error);
    }
  };

  const handleToggleCabinet = async () => {
    if (!actionCabinet) return;

    try {
      const action = actionCabinet.is_active ? 'disable' : 'enable';
      await superAdminService.toggleCabinet(actionCabinet.id, {
        action,
        reason: toggleReason || undefined
      });
      setShowToggleDialog(false);
      setActionCabinet(null);
      setToggleReason('');
      fetchCabinets();
    } catch (error) {
      console.error('Error toggling cabinet:', error);
      handleServerError(error);
    }
  };

  const handleApproval = async () => {
    if (!actionCabinet) return;

    try {
      await superAdminService.approveCabinet(actionCabinet.id, {
        action: approvalAction,
        reason: approvalAction === 'reject' ? (approvalReason || undefined) : undefined,
      });
      setShowApprovalDialog(false);
      setActionCabinet(null);
      setApprovalReason('');
      fetchCabinets();
    } catch (error) {
      console.error('Error deciding on cabinet demande:', error);
      handleServerError(error);
    }
  };

  const handleDeleteCabinet = async () => {
    if (!actionCabinet) return;

    try {
      await superAdminService.deleteCabinet(actionCabinet.id);
      setShowDeleteDialog(false);
      setActionCabinet(null);
      fetchCabinets();
    } catch (error) {
      console.error('Error deleting cabinet:', error);
      handleServerError(error);
    }
  };

  // Define columns for DataTable
  const columns: ColumnDef<SuperAdminCabinet>[] = useMemo(() => [
    {
      id: 'name',
      accessorKey: 'name',
      meta: { className: 'min-w-[160px]' },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('name')} />
      ),
      cell: ({ row }) => {
        const cabinet = row.original;
        return (
          <div className="flex flex-col">
            <span className="font-medium">{cabinet.name}</span>
            <span className="text-xs text-muted-foreground font-mono">#{cabinet.id}</span>
          </div>
        );
      },
    },
    {
      id: 'admin_info',
      accessorKey: 'admin_name',
      meta: { className: 'min-w-[160px]' },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('cabinet_admin') || 'Admin / Contact'} />
      ),
      cell: ({ row }) => {
        const cabinet = row.original;
        return (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-foreground">
              {cabinet.owner?.name || '-'}
            </span>
            {cabinet.owner?.phone && (
              <span className="text-xs text-muted-foreground flex items-center gap-1 font-mono">
                <Phone className="h-3 w-3 text-primary inline-block shrink-0" />
                <span>{cabinet.owner.phone}</span>
              </span>
            )}
            {cabinet.owner?.email && !cabinet.owner?.phone && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Mail className="h-3 w-3 inline-block shrink-0" />
                <span className="truncate max-w-[140px]">{cabinet.owner.email}</span>
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'approval_status',
      accessorKey: 'approval_status',
      meta: { className: 'min-w-[130px]' },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('approval_status') || 'Demande'} />
      ),
      cell: ({ row }) => {
        const s = row.original.approval_status || 'approved';
        if (s === 'pending') {
          return (
            <Badge variant="outline" className="border-amber-400 text-amber-600 flex items-center gap-1 w-fit">
              <Clock3 className="h-3 w-3" /> {t('approval_pending') || 'Pending'}
            </Badge>
          );
        }
        if (s === 'rejected') {
          return (
            <Badge variant="outline" className="border-red-400 text-red-600 flex items-center gap-1 w-fit">
              <XCircle className="h-3 w-3" /> {t('approval_rejected') || 'Rejected'}
            </Badge>
          );
        }
        return (
          <Badge variant="outline" className="border-green-400 text-green-600 flex items-center gap-1 w-fit">
            <CheckCircle2 className="h-3 w-3" /> {t('approval_approved') || 'Approved'}
          </Badge>
        );
      },
    },
    {
      id: 'is_active',
      accessorKey: 'is_active',
      meta: { className: 'min-w-[110px]' },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('status')} />
      ),
      cell: ({ row }) => {
        const cabinet = row.original;
        return (
          <Badge variant={cabinet.is_active ? 'default' : 'secondary'}>
            {cabinet.is_active ? t('active') : t('disabled')}
          </Badge>
        );
      },
    },
    {
      id: 'total_users',
      accessorKey: 'total_users',
      meta: { className: 'min-w-[110px]' },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('total_users')} />
      ),
      cell: ({ row }) => {
        const totalUsers = parseInt(String(row.original.total_users || 0)) || 0;
        return <span className="text-center font-medium block">{totalUsers}</span>;
      },
    },
    {
      id: 'total_cases',
      accessorKey: 'total_cases',
      meta: { className: 'min-w-[110px]' },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('total_cases')} />
      ),
      cell: ({ row }) => {
        const totalCases = parseInt(String(row.original.total_cases || 0)) || 0;
        return <span className="text-center font-medium block">{totalCases}</span>;
      },
    },
    {
      id: 'created_at',
      accessorKey: 'created_at',
      meta: { className: 'min-w-[140px] whitespace-nowrap' },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('created_at') || 'Date de création'} />
      ),
      cell: ({ row }) => {
        const val = row.original.created_at;
        return (
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {val ? new Date(val).toLocaleDateString() : '-'}
          </span>
        );
      },
    },
    {
      id: 'updated_at',
      accessorKey: 'updated_at',
      meta: { className: 'min-w-[140px] whitespace-nowrap' },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('updated_at') || 'Dernière modification'} />
      ),
      cell: ({ row }) => {
        const val = row.original.updated_at;
        return (
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {val ? new Date(val).toLocaleDateString() : '-'}
          </span>
        );
      },
    },
    {
      id: 'last_activity',
      accessorKey: 'last_activity',
      meta: { className: 'min-w-[150px] whitespace-nowrap' },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('last_activity') || 'Dernière activité'} />
      ),
      cell: ({ row }) => {
        const val = row.original.last_activity;
        return (
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {val ? new Date(val).toLocaleString() : '-'}
          </span>
        );
      },
    },
    {
      id: 'actions',
      meta: { className: 'w-[80px]' },
      header: () => <div className="text-center">{t('actions')}</div>,
      cell: ({ row }) => {
        const cabinet = row.original;
        return (
          <div className="flex justify-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleViewCabinet(cabinet)}>
                  <Eye className="mr-2 h-4 w-4" />
                  {t('view_details')}
                </DropdownMenuItem>
                {cabinet.approval_status === 'pending' && (
                  <>
                    <DropdownMenuItem onClick={() => { setActionCabinet(cabinet); setApprovalAction('approve'); setShowApprovalDialog(true); }}>
                      <CheckCircle2 className="mr-2 h-4 w-4 text-green-600" />
                      <span className="text-green-600">{t('approve') || 'Approve'}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { setActionCabinet(cabinet); setApprovalAction('reject'); setShowApprovalDialog(true); }}>
                      <XCircle className="mr-2 h-4 w-4 text-red-600" />
                      <span className="text-red-600">{t('reject') || 'Reject'}</span>
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuItem onClick={() => { setActionCabinet(cabinet); setShowToggleDialog(true); }}>
                  {cabinet.is_active ? (
                    <>
                      <PowerOff className="mr-2 h-4 w-4 text-red-600" />
                      <span className="text-red-600">{t('disable')}</span>
                    </>
                  ) : (
                    <>
                      <Power className="mr-2 h-4 w-4 text-green-600" />
                      <span className="text-green-600">{t('enable')}</span>
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setActionCabinet(cabinet); setShowDeleteDialog(true); }} className="text-red-600">
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ], [t]);

  if (loading) {
    return (
      <div className="flex h-screen">
        <div className="flex-1 min-w-0">
          <Main>
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-muted-foreground">{t('loading')}</p>
              </div>
            </div>
          </Main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      <div className="flex-1 min-w-0">
        <Main>
          <div className="space-y-6">
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <PageHeader
                leading={
                  <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Building2 className="h-5 w-5" />
                  </span>
                }
                title={t('cabinets_management')}
                description={t('manage_system_cabinets')}
                actions={
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="me-2 h-4 w-4" />
                      {t('create_cabinet')}
                    </Button>
                  </DialogTrigger>
                }
              />
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('create_new_cabinet')}</DialogTitle>
                  <DialogDescription>
                    {t('create_cabinet_description')}
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="name" className="text-right">
                      {t('name')}
                    </Label>
                    <Input
                      id="name"
                      value={newCabinet.name}
                      onChange={(e) => setNewCabinet({ ...newCabinet, name: e.target.value })}
                      className="col-span-3"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                    {t('cancel')}
                  </Button>
                  <Button onClick={handleCreateCabinet} disabled={!newCabinet.name.trim()}>
                    {t('create')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Approval status filter */}
            <div className="flex gap-2">
              {(['pending', 'all', 'approved', 'rejected'] as const).map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={approvalFilter === s ? 'default' : 'outline'}
                  onClick={() => setApprovalFilter(s)}
                >
                  {s === 'all' ? (t('all') || 'All') : t(`approval_${s}`) || s}
                </Button>
              ))}
            </div>

            {/* DataTable with integrated pagination */}
            <DataTable
              columns={columns}
              data={cabinets}
              pages={pagesCnt}
              params={params}
              setParams={setParams}
              showPagination={true}
              storageKey="super-admin-cabinets-table"
            />

            {cabinets.length === 0 && (
              <div className="text-center py-12">
                <Building2 className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                  {t('no_cabinets')}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('get_started_create_cabinet')}
                </p>
              </div>
            )}
          </div>

          {/* Cabinet Details Dialog */}
          <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="text-2xl font-bold">{selectedCabinet?.name}</DialogTitle>
                    <DialogDescription className="mt-1">
                      {t('cabinet_detailed_information')}
                    </DialogDescription>
                  </div>
                  <Badge
                    variant={selectedCabinet?.is_active ? 'default' : 'secondary'}
                    className="text-sm px-3 py-1"
                  >
                    {selectedCabinet?.is_active ? t('active') : t('disabled')}
                  </Badge>
                </div>
              </DialogHeader>
              {selectedCabinet && (
                <div className="space-y-6 py-4">
                  {/* Cabinet Overview */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="border-indigo-200 bg-indigo-50/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-indigo-700 flex items-center gap-2">
                          <UserIcon className="h-4 w-4" />
                          {t('cabinet_admin') || 'Admin'}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-base font-bold text-indigo-900 truncate">
                          {selectedCabinet.admin_name || '-'}
                        </div>
                        {selectedCabinet.admin_phone && (
                          <p className="text-xs font-mono text-indigo-700 mt-1 flex items-center gap-1">
                            <Phone className="h-3.5 w-3.5 shrink-0" />
                            <span>{selectedCabinet.admin_phone}</span>
                          </p>
                        )}
                        {selectedCabinet.admin_email && (
                          <p className="text-xs text-indigo-600 mt-0.5 flex items-center gap-1">
                            <Mail className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{selectedCabinet.admin_email}</span>
                          </p>
                        )}
                      </CardContent>
                    </Card>

                    <Card className="border-blue-200 bg-blue-50/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-blue-700 flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          {t('total_users')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-blue-900">{selectedCabinet.total_users}</div>
                        <p className="text-xs text-blue-600 mt-1">{t('registered_users')}</p>
                      </CardContent>
                    </Card>

                    <Card className="border-green-200 bg-green-50/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-green-700 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          {t('total_cases')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-green-900">{selectedCabinet.total_cases}</div>
                        <p className="text-xs text-green-600 mt-1">{t('all_cases')}</p>
                      </CardContent>
                    </Card>

                    <Card className="border-purple-200 bg-purple-50/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-purple-700 flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          {t('cabinet_id')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-lg font-mono text-purple-900">#{selectedCabinet.id}</div>
                        <p className="text-xs text-purple-600 mt-1">{t('unique_identifier')}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Detailed Statistics */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Users className="h-5 w-5 text-blue-600" />
                          {t('users_statistics')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-slate-500 rounded-full"></div>
                              <span className="text-sm font-medium">{t('total_users')}</span>
                            </div>
                            <span className="font-bold text-slate-700">{selectedCabinet.total_users}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-slate-500 rounded-full"></div>
                              <span className="text-sm font-medium">{t('active_users')}</span>
                            </div>
                            <span className="font-bold text-slate-700">{selectedCabinet.active_users}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-slate-500 rounded-full"></div>
                              <span className="text-sm font-medium">{t('admin_users')}</span>
                            </div>
                            <span className="font-bold text-slate-700">{selectedCabinet.admin_users}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <FileText className="h-5 w-5 text-green-600" />
                          {t('cases_statistics')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                              <span className="text-sm font-medium">{t('open_cases')}</span>
                            </div>
                            <span className="font-bold text-green-700">{selectedCabinet.open_cases}</span>
                          </div>

                          <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                              <span className="text-sm font-medium">{t('archived_cases')}</span>
                            </div>
                            <span className="font-bold text-gray-700">{selectedCabinet.archived_cases}</span>
                          </div>

                          <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                              <span className="text-sm font-medium">{t('cases_this_month')}</span>
                            </div>
                            <span className="font-bold text-blue-700">{selectedCabinet.cases_this_month}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <HardDrive className="h-5 w-5 text-orange-600" />
                          {t('file_storage_statistics')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-3">
                          <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                              <span className="text-sm font-medium">{t('total_files')}</span>
                            </div>
                            <span className="font-bold text-orange-700">{selectedCabinet.file_storage_info?.total_files || 0}</span>
                          </div>

                          <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                              <span className="text-sm font-medium">{t('storage_size')}</span>
                            </div>
                            <span className="font-bold text-blue-700">{selectedCabinet.file_storage_info?.total_size_formatted || '0 B'}</span>
                          </div>

                          <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                              <span className="text-sm font-medium">{t('recent_files_30_days')}</span>
                            </div>
                            <span className="font-bold text-purple-700">{selectedCabinet.file_storage_info?.recent_files_30_days || 0}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}
              <DialogFooter className="flex justify-between">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setActionCabinet(selectedCabinet);
                      setShowDetailsDialog(false);
                      setShowToggleDialog(true);
                    }}
                    className="flex items-center gap-2"
                  >
                    {selectedCabinet?.is_active ? (
                      <>
                        <PowerOff className="h-4 w-4" />
                        {t('disable')}
                      </>
                    ) : (
                      <>
                        <Power className="h-4 w-4" />
                        {t('enable')}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      setActionCabinet(selectedCabinet);
                      setShowDetailsDialog(false);
                      setShowDeleteDialog(true);
                    }}
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    {t('delete')}
                  </Button>
                </div>
                <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
                  {t('close')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Approve / Reject Demande Dialog */}
          <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {approvalAction === 'approve' ? (t('approve_cabinet') || 'Approve demande') : (t('reject_cabinet') || 'Reject demande')}
                </DialogTitle>
                <DialogDescription>
                  <strong>{actionCabinet?.name}</strong>
                  {actionCabinet?.owner && (
                    <span className="block mt-2 text-sm">
                      {actionCabinet.owner.name} — {actionCabinet.owner.phone || actionCabinet.owner.email}
                    </span>
                  )}
                  {actionCabinet?.availability_days?.length ? (
                    <span className="block mt-1 text-sm text-muted-foreground">
                      {(t('availability_days') || 'Available days')}: {actionCabinet.availability_days.join(', ')} — {actionCabinet.availability_slot}
                    </span>
                  ) : null}
                </DialogDescription>
              </DialogHeader>
              {approvalAction === 'reject' && (
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="approval-reason" className="text-right">
                      {t('reason')} ({t('optional')})
                    </Label>
                    <Textarea
                      id="approval-reason"
                      value={approvalReason}
                      onChange={(e) => setApprovalReason(e.target.value)}
                      className="col-span-3"
                      placeholder={t('enter_reason')}
                    />
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleApproval}
                  variant={approvalAction === 'reject' ? 'destructive' : 'default'}
                >
                  {approvalAction === 'approve' ? (t('approve') || 'Approve') : (t('reject') || 'Reject')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Toggle Cabinet Dialog */}
          <Dialog open={showToggleDialog} onOpenChange={setShowToggleDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {actionCabinet?.is_active ? t('disable_cabinet') : t('enable_cabinet')}
                </DialogTitle>
                <DialogDescription>
                  {actionCabinet?.is_active
                    ? t('disable_cabinet_description')
                    : t('enable_cabinet_description')
                  }
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="reason" className="text-right">
                    {t('reason')} ({t('optional')})
                  </Label>
                  <Textarea
                    id="reason"
                    value={toggleReason}
                    onChange={(e) => setToggleReason(e.target.value)}
                    className="col-span-3"
                    placeholder={t('enter_reason')}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowToggleDialog(false)}>
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleToggleCabinet}
                  variant={actionCabinet?.is_active ? 'destructive' : 'default'}
                >
                  {actionCabinet?.is_active ? t('disable') : t('enable')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Cabinet Dialog */}
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('delete_cabinet')}</DialogTitle>
                <DialogDescription>
                  {t('delete_cabinet_warning')} <strong>{actionCabinet?.name}</strong>. {t('action_cannot_be_undone')}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                  {t('cancel')}
                </Button>
                <Button variant="destructive" onClick={handleDeleteCabinet}>
                  {t('delete')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Main>
      </div>
    </div>
  );
}
